import { useId, useState } from "react";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput, { type OutlinedInputProps } from "@mui/material/OutlinedInput";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { colorTokens } from "../theme/tokens";

/**
 * A password input with a show/hide toggle, styled like the SSO
 * reference: a static bold label above the field (see LabeledTextField),
 * not MUI's floating label, plus a teal visibility toggle.
 */
export function PasswordField({
  label,
  id,
  ...inputProps
}: Omit<OutlinedInputProps, "type" | "endAdornment"> & { label: string }) {
  const [visible, setVisible] = useState(false);
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  return (
    <FormControl fullWidth={inputProps.fullWidth ?? true} error={inputProps.error} variant="outlined">
      <FormLabel htmlFor={fieldId}>{label}</FormLabel>
      <OutlinedInput
        {...inputProps}
        id={fieldId}
        type={visible ? "text" : "password"}
        notched={false}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label={visible ? "Hide password" : "Show password"}
              aria-pressed={visible}
              onClick={() => setVisible((current) => !current)}
              edge="end"
              size="small"
              sx={{ color: colorTokens.brand }}
            >
              {visible ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
      />
    </FormControl>
  );
}
